/**
 * Interaction Step Renderer - Handles user interactions (mocked or real)
 */

import React, { useEffect, useState } from 'react';
import { InteractionConfig, FlowViewConfig, FlowContext } from '../core/types';
import { GuidedFlowController } from '../core/GuidedFlowController';
import { motion, AnimatePresence } from 'motion/react';
import confetti from 'canvas-confetti';
import ReactMarkdown from 'react-markdown';

export interface InteractionStepRendererProps {
  config: InteractionConfig;
  viewConfig?: FlowViewConfig;
  context: FlowContext;
  controller: GuidedFlowController;
  onComplete: () => void;
  autoProgressEnabled?: boolean;
}

export const InteractionStepRenderer: React.FC<InteractionStepRendererProps> = ({
  config,
  viewConfig,
  context,
  controller,
  onComplete,
  autoProgressEnabled = false,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  const providers = controller.getProviders();

  // Navigate to view if provided
  useEffect(() => {
    if (viewConfig?.component) {
      providers.navigation.navigateToView(viewConfig.component, viewConfig.props);
    }
  }, [viewConfig, providers.navigation]);

  // Handle mocked interaction
  const handleMockedInteraction = async () => {
    if (isProcessing || isComplete) return;

    setIsProcessing(true);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 10;
      });
    }, (config.mockDelay || 1500) / 10);

    // Perform the mocked action
    setTimeout(async () => {
      clearInterval(progressInterval);
      setProgress(100);

      // Handle different action types
      switch (config.action.type) {
        case 'upload':
          if (config.action.files) {
            // Mock file upload
            for (const file of config.action.files) {
              await providers.data.uploadFile(file);
            }
          }
          break;

        case 'click':
          if (config.action.clickTarget) {
            // Mock click action
            providers.ui.simulateClick(config.action.clickTarget);
          }
          break;

        case 'form':
          if (config.action.formData) {
            // Mock form submission
            await providers.data.submitForm(config.action.formData);
          }
          break;
      }

      setIsProcessing(false);
      setIsComplete(true);

      // Show success feedback
      if (config.onSuccess) {
        if (config.onSuccess.message) {
          providers.notifications.showSuccess(config.onSuccess.message);
        }
        if (config.onSuccess.celebration) {
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
          });
        }
        if (config.onSuccess.sound) {
          // Play success sound (if implemented)
        }
      }

      // Auto-advance after success (only if toggle is enabled)
      if (autoProgressEnabled) {
        setTimeout(() => {
          onComplete();
        }, 1500);
      }
    }, config.mockDelay || 1500);
  };

  // Auto-start mocked interaction
  useEffect(() => {
    if (config.mocked) {
      // Give user time to see the view before starting
      setTimeout(() => {
        handleMockedInteraction();
      }, 1000);
    }
  }, [config.mocked]);

  return (
    <div className="flex h-full">
      {/* Main Content */}
      <div className="flex-1 relative">
        {/* Processing Overlay */}
        <AnimatePresence>
          {isProcessing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
            >
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-8 max-w-md w-full">
                <div className="text-center">
                  <div className="text-white text-lg font-medium mb-4">
                    {config.action.type === 'upload' && 'Uploading files...'}
                    {config.action.type === 'click' && 'Processing...'}
                    {config.action.type === 'form' && 'Submitting...'}
                  </div>

                  {/* Progress bar */}
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-4">
                    <motion.div
                      className="h-full bg-primary"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>

                  <div className="text-white/70 text-sm">
                    {progress}%
                  </div>

                  {/* File list for uploads */}
                  {config.action.type === 'upload' && config.action.files && (
                    <div className="mt-4 space-y-2">
                      {config.action.files.map((file, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-3 text-left text-sm text-white/80"
                        >
                          <div className="w-8 h-8 rounded bg-primary/20 flex items-center justify-center text-lg">
                            📄
                          </div>
                          <div className="flex-1">
                            <div className="font-medium">{file.name}</div>
                            <div className="text-white/50 text-xs">
                              {(file.size / 1024).toFixed(1)} KB
                            </div>
                          </div>
                          {progress === 100 && (
                            <div className="text-green-400">✓</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Success State */}
        <AnimatePresence>
          {isComplete && config.onSuccess && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none"
            >
              <div className="bg-green-500/20 backdrop-blur-md border border-green-500/50 rounded-xl p-6 text-center">
                <div className="text-4xl mb-2">✨</div>
                <div className="text-white text-lg font-medium">
                  {config.onSuccess.message}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Assistant Panel */}
      {viewConfig?.assistant && (
        <motion.div
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-96 bg-white/5 backdrop-blur-md border-l border-white/10 p-6"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-2xl">
              🤖
            </div>
            <div>
              <div className="text-white font-medium">Equal IQ Assistant</div>
              <div className="text-white/50 text-sm">AI Guide</div>
            </div>
          </div>
          <div className="text-white/90 text-sm leading-relaxed prose prose-invert prose-sm max-w-none">
            <ReactMarkdown>{viewConfig.assistant.message}</ReactMarkdown>
          </div>
        </motion.div>
      )}
    </div>
  );
};
